import { InvoiceModel, PaymentOriginEnum } from '@frontend/api-invoices';
import { PaymentMethod, PaymentPlanDetailsResponse, PaymentPlanPaymentType } from '@frontend/api-payment-plans';
import {
  CreatePaymentIntentBody,
  PaymentMethodTypeEnum,
  SetupFutureUsage,
  makePaymentSync as makePaymentSyncApi,
} from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { genUUIDV4 } from '@frontend/string';
import { useAlert } from '@frontend/design-system';
import { isPaidInvoice } from '../utils';

export enum PaymentIntentSyncErrors {
  RequestAlreadyCompleted = 'request is already completed',
}

const getErrorCause = (err: unknown) => {
  let cause = '';
  if (err instanceof HttpError && err.data && typeof err.data !== 'string' && 'message' in err.data) {
    const errorMessage = JSON.parse(err.data.message);
    cause = errorMessage.cause ?? '';
  }
  return cause;
};
export interface UseCreatePaymentIntentSyncOptions {
  amount: number;
  origin: PaymentOriginEnum;
  locationId: string | undefined;
  personId: string | undefined;
  email: string | undefined;
  invoiceId?: string;
  paymentMethod?: PaymentMethod;
  setupFutureUsage?: SetupFutureUsage | null;
  planDetails?: PaymentPlanDetailsResponse;
  receiptEmail?: string;
  cardholderName?: string;

  onInvoiceAlreadyPaid?: () => void;
  onRefetchInvoice?: () => Promise<InvoiceModel | undefined>;
}

export const useCreatePaymentIntentSync = ({
  amount,
  email,
  personId,
  locationId,
  origin,
  paymentMethod,
  setupFutureUsage,
  planDetails,
  invoiceId,
  receiptEmail,
  cardholderName,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: UseCreatePaymentIntentSyncOptions) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const getPaymentPayload = (optionalPaymentMethod?: Partial<PaymentMethod>) => {
    const isACHPayment = (optionalPaymentMethod?.type || paymentMethod?.type) === 'us_bank_account';
    const paymentMethodId = optionalPaymentMethod?.id || paymentMethod?.id || '';

    const payment: CreatePaymentIntentBody = {
      amount,
      paymentId: genUUIDV4(),
      origin,
      ...(paymentMethodId ? { paymentMethodId } : {}),
      ...(setupFutureUsage ? { setupFutureUsage } : {}),
      ...(receiptEmail ? { receiptEmail } : {}),
      ...(cardholderName ? { cardholderName } : {}),
      paymentMethodType: isACHPayment ? PaymentMethodTypeEnum.ACH : PaymentMethodTypeEnum.CARD,
      ...(personId || email
        ? {
            person: {
              ...(personId ? { id: personId } : {}),
              ...(email ? { email } : {}),
            },
          }
        : {}),
      ...(planDetails
        ? {
            paymentPlanPayment: {
              planId: planDetails.id,
              paymentType: PaymentPlanPaymentType.Balance,
            },
          }
        : {}),
    };

    return payment;
  };

  const createPaymentIntent = async ({ paymentMethod }: { paymentMethod?: Partial<PaymentMethod> } = {}) => {
    const payment = getPaymentPayload(paymentMethod);

    if (!locationId) {
      console.error('the locationId is empty', locationId);
    } else {
      try {
        if (onRefetchInvoice) {
          const invoice = await onRefetchInvoice();
          if (isPaidInvoice(invoice)) {
            throw new Error(PaymentIntentSyncErrors.RequestAlreadyCompleted);
          }
        }

        const { paymentId, paymentIntent } = await makePaymentSyncApi(payment, locationId, invoiceId);
        /* Temporary fix TODO: arobinson
         * Refetch invoice after intent creation to get correct surcharge amounts.
         * Remove once backend is updated to return correct amounts in initial invoice lookup.
         */
        if (onRefetchInvoice) {
          await onRefetchInvoice();
        }

        return { paymentId, paymentIntent };
      } catch (err) {
        try {
          if (
            (err instanceof Error && err.message === PaymentIntentSyncErrors.RequestAlreadyCompleted) ||
            getErrorCause(err) === PaymentIntentSyncErrors.RequestAlreadyCompleted
          ) {
            errorAlert(t('Invoice is already paid.'));
            onRefetchInvoice?.();
            onInvoiceAlreadyPaid?.();
          } else {
            // throwing to show the generic error message in next catch block
            throw err;
          }
        } catch (err) {
          errorAlert(t('Error retrieving payment details.'));
        }
      }
    }

    return { paymentId: undefined, paymentIntent: undefined };
  };

  return { createPaymentIntent };
};
